import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { apiService, apiHelpers } from '../services/api';
import ArticleCard from '../components/ArticleCard';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import './Reader.css';

const PER_PAGE = 20;
const DENSITY_KEY = 'paperboy.density';
const SAVED_KEY = 'paperboy.savedIds';
const DENSITIES = ['compact', 'default', 'roomy'];

const safeGetItem = (key) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    if (typeof window.localStorage.getItem !== 'function') return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key, value) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (typeof window.localStorage.setItem !== 'function') return;
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
};

const readDensity = () => {
  const stored = safeGetItem(DENSITY_KEY);
  return DENSITIES.includes(stored) ? stored : 'default';
};

const readSavedIds = () => {
  const raw = safeGetItem(SAVED_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const formatMastheadDate = (now = new Date()) =>
  now
    .toLocaleDateString('en-US', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    .replace(',', ' ·');

const firstName = (user) => {
  if (!user) return 'you';
  const name = user.full_name || user.name || user.username || user.email || '';
  return String(name).trim().split(/[\s@]/)[0] || 'you';
};

const articleTopic = (article) => {
  if (article?.tags && article.tags.length > 0) return article.tags[0];
  return 'general';
};

const articleScore = (article) => {
  if (!article?.published) return 50;
  const ageHours = (Date.now() - new Date(article.published).getTime()) / 36e5;
  if (Number.isNaN(ageHours)) return 50;
  return Math.max(20, Math.min(99, Math.round(99 - ageHours * 1.5)));
};

const safeHttpUrl = (url) => (/^https?:\/\//i.test(url ?? '') ? url : null);

const articleTime = (article) => {
  if (!article?.published) return 'recent';
  try {
    return formatDistanceToNow(new Date(article.published), { addSuffix: false });
  } catch {
    return 'recent';
  }
};

const HomePage = () => {
  const { user } = useAuth();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  const [activeTopic, setActiveTopic] = useState('all');
  const [availableTopics, setAvailableTopics] = useState([]);
  const [totalSourceCount, setTotalSourceCount] = useState(0);

  const [density, setDensity] = useState(readDensity);
  const [savedIds, setSavedIds] = useState(readSavedIds);

  const tagFilter = activeTopic !== 'all' && activeTopic !== 'saved' ? activeTopic : null;

  const loadArticles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: currentPage, per_page: PER_PAGE };
      if (tagFilter) params.tags = tagFilter;
      const response = await apiService.getArticles(params);
      const data = response.data || {};
      setArticles(data.articles || []);
      setTotal(data.total || 0);
      setHasNext(Boolean(data.has_next));
      setHasPrev(Boolean(data.has_prev));
    } catch (err) {
      console.error('Error loading articles:', err);
      setError(apiHelpers.formatError(err));
    } finally {
      setLoading(false);
    }
  }, [currentPage, tagFilter]);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getTags()
      .then((response) => {
        if (cancelled) return;
        setAvailableTopics(Array.isArray(response.data) ? response.data : []);
      })
      .catch((err) => console.error('Error loading tags:', err));
    apiService
      .getSources()
      .then((response) => {
        if (cancelled) return;
        const sources = Array.isArray(response.data) ? response.data : [];
        setTotalSourceCount(sources.length);
      })
      .catch((err) => console.error('Error loading sources:', err));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  useEffect(() => {
    safeSetItem(DENSITY_KEY, density);
  }, [density]);

  useEffect(() => {
    safeSetItem(SAVED_KEY, JSON.stringify(savedIds));
  }, [savedIds]);

  const visibleStories = useMemo(() => {
    if (activeTopic === 'saved') {
      return articles.filter((a) => savedIds.includes(String(a.id)));
    }
    return articles;
  }, [articles, activeTopic, savedIds]);

  const lead = visibleStories[0];
  const rest = visibleStories.slice(1);
  const pageTitle = activeTopic === 'all'
    ? 'Front page'
    : activeTopic === 'saved'
      ? 'Saved stories'
      : activeTopic;

  const toggleSave = useCallback((id) => {
    const stringId = String(id);
    setSavedIds((prev) =>
      prev.includes(stringId) ? prev.filter((x) => x !== stringId) : [...prev, stringId],
    );
  }, []);

  const selectTopic = (topic) => {
    setActiveTopic(topic);
    setCurrentPage(1);
  };

  const refresh = () => {
    loadArticles();
  };

  const mastheadDate = useMemo(() => formatMastheadDate(), []);

  if (loading && articles.length === 0) {
    return (
      <div className="container">
        <LoadingSpinner text="Loading latest articles..." />
      </div>
    );
  }

  return (
    <div className={`pb-reader pb-reader--${density}`}>
      <aside className="pb-sidebar" aria-label="Reader sidebar">
        <div className="pb-wordmark">
          [paperboy]
          <span className="pb-wordmark-accent">.</span>
        </div>

        <div className="pb-sidebar-section">
          <div className="pb-mono-tag">Your feed</div>
          <div className="pb-nav-list">
            <button
              type="button"
              className={`pb-nav-button ${activeTopic === 'all' ? 'is-active' : ''}`}
              onClick={() => selectTopic('all')}
              aria-pressed={activeTopic === 'all'}
            >
              <span>Front page</span>
              <span className="pb-nav-count">{total}</span>
            </button>
            <button
              type="button"
              className={`pb-nav-button ${activeTopic === 'saved' ? 'is-active' : ''}`}
              onClick={() => selectTopic('saved')}
              aria-pressed={activeTopic === 'saved'}
            >
              <span>Saved</span>
              <span className="pb-nav-count">{savedIds.length}</span>
            </button>
          </div>
        </div>

        <div className="pb-sidebar-section">
          <div className="pb-mono-tag">Topics</div>
          <div className="pb-nav-list pb-topic-strip" role="list">
            {availableTopics.slice(0, 12).map((topic) => {
              const isActive = activeTopic === topic;
              return (
                <button
                  type="button"
                  key={topic}
                  className={`pb-topic-button ${isActive ? 'is-active' : ''}`}
                  onClick={() => selectTopic(topic)}
                  aria-pressed={isActive}
                >
                  <span className="pb-topic-dot" aria-hidden="true" />
                  {topic}
                </button>
              );
            })}
            <Link to="/search" className="pb-topic-button" style={{ color: 'rgba(0,0,0,0.5)' }}>
              + More topics
            </Link>
          </div>
        </div>

        <div className="pb-live-card">
          <div className="pb-mono-tag">● Live</div>
          <div className="pb-live-card-body">
            {totalSourceCount.toLocaleString()} sources
            <br />
            {total.toLocaleString()} stories ranked
            <br />
            Updated just now
          </div>
        </div>
      </aside>

      <main className={`pb-main pb-main--${density}`}>
        <header className="pb-masthead">
          <div className="pb-masthead-meta">{mastheadDate}</div>
          <h1 className="pb-masthead-title">
            {pageTitle}{' '}
            <span className="pb-masthead-title-accent">· for {firstName(user)}</span>
          </h1>
          <div className="pb-masthead-meta pb-masthead-meta--right">
            {new Date().toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            })}{' '}
            · {Math.max(2, Math.min(20, total))} min read
          </div>
        </header>

        <div className="pb-stats">
          <span className="pb-stats-live">
            <span className="pb-stats-dot" aria-hidden="true" />
            Live
          </span>
          <span aria-hidden="true">·</span>
          <span>Scoured {totalSourceCount.toLocaleString()} sources</span>
          <span aria-hidden="true">·</span>
          <span>{total} stories ranked</span>
          {activeTopic !== 'all' && (
            <>
              <span aria-hidden="true">·</span>
              <span>filtered by {pageTitle}</span>
            </>
          )}
          <span aria-hidden="true">·</span>
          <span>ranked by signal</span>
          <div className="pb-stats-spacer">
            <div className="pb-density" role="group" aria-label="Density">
              {DENSITIES.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`pb-density-button ${density === opt ? 'is-active' : ''}`}
                  onClick={() => setDensity(opt)}
                  aria-pressed={density === opt}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="pb-chip"
            onClick={refresh}
            disabled={loading}
            aria-label="Refresh"
          >
            ↻ Re-rank
          </button>
        </div>

        {error && (
          <div className="pb-error" role="alert">
            <span>
              <strong>Error:</strong> {error}
            </span>
            <button type="button" className="pb-chip" onClick={refresh}>
              Try Again
            </button>
          </div>
        )}

        {visibleStories.length === 0 && !error && (
          <div className="pb-empty">
            {activeTopic === 'saved'
              ? 'No saved stories yet. Tap ☆ on any story to keep it here.'
              : 'No articles found. Try a different topic or check back later.'}
            {activeTopic !== 'saved' && (
              <div style={{ marginTop: 12 }}>
                <Link to="/search" className="pb-chip">
                  Advanced Search
                </Link>
              </div>
            )}
          </div>
        )}

        {visibleStories.length > 0 && (
          <div className="pb-stories">
            {lead && (
              <article className="pb-lead">
                <div className="pb-lead-row">
                  <span className="pb-mono-tag pb-lead-tag">
                    ▸ Top of feed · {articleTopic(lead).toUpperCase()} · {lead.source_name || '1 source'}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSave(lead.id);
                    }}
                    className={`pb-save-button ${savedIds.includes(String(lead.id)) ? 'is-saved' : ''}`}
                    aria-label={savedIds.includes(String(lead.id)) ? 'Unsave story' : 'Save story'}
                  >
                    {savedIds.includes(String(lead.id)) ? '★ SAVED' : '☆ SAVE'}
                  </button>
                </div>
                <h2 className="pb-lead-headline">
                  <Link
                    to={`/article/${lead.id}`}
                    className="pb-lead-link"
                    aria-label={`Lead story: ${lead.title || 'Untitled story'}`}
                  >
                    {lead.title || 'Untitled story'}
                  </Link>
                </h2>
                {lead.summary && (
                  <p className="pb-lead-blurb">
                    {lead.summary.length > 240
                      ? `${lead.summary.slice(0, 240).trim()}…`
                      : lead.summary}
                  </p>
                )}
                <div className="pb-outlets">
                  {safeHttpUrl(lead.link) && (
                    <a
                      href={safeHttpUrl(lead.link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pb-outlet-chip"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.source_name || 'source'} ↗
                    </a>
                  )}
                  {(lead.tags || []).slice(1, 4).map((tag) => (
                    <span key={tag} className="pb-outlet-chip">
                      {tag}
                    </span>
                  ))}
                  {(lead.tags || []).length > 4 && (
                    <span className="pb-outlet-chip--more">
                      +{lead.tags.length - 4} more
                    </span>
                  )}
                </div>
                <div className="pb-lead-footer">
                  <span>
                    Signal score · <span className="pb-signal">{articleScore(lead)}/100</span>
                  </span>
                  <span>{articleTime(lead)} · 4 min read</span>
                </div>
              </article>
            )}

            <div className="pb-list" aria-label="Ranked stories">
              {rest.map((article) => (
                <ArticleCard
                  key={article.id}
                  article={article}
                  saved={savedIds.includes(String(article.id))}
                  onToggleSave={toggleSave}
                />
              ))}
              {rest.length === 0 && lead && (
                <div className="pb-empty" style={{ marginTop: 0 }}>
                  Long tail loading on the next drop.
                </div>
              )}
            </div>
          </div>
        )}

        {(hasPrev || hasNext) && (
          <nav className="pb-pagination" aria-label="Pagination">
            <button
              type="button"
              className="pb-chip"
              disabled={!hasPrev || loading}
              onClick={() => {
                setCurrentPage((p) => Math.max(1, p - 1));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              ← Previous
            </button>
            <span className="pb-mono-tag" style={{ alignSelf: 'center' }}>
              Page {currentPage}
            </span>
            <button
              type="button"
              className="pb-chip"
              disabled={!hasNext || loading}
              onClick={() => {
                setCurrentPage((p) => p + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              Next →
            </button>
          </nav>
        )}
      </main>
    </div>
  );
};

export default HomePage;
